import {
  ArrowDownTrayIcon,
  ArrowUpTrayIcon,
  DocumentIcon,
  DocumentTextIcon,
  EllipsisHorizontalIcon,
  EyeIcon,
  LinkIcon,
  PaperClipIcon,
  PencilIcon,
  PhotoIcon,
  PlusIcon,
  TableCellsIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/20/solid";
import { useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { Link } from "react-router";
import { api, errorMessage } from "@/lib/api";
import { useProject } from "@/lib/project";
import { keys, useFiles } from "@/lib/queries";
import type { EntityType, FileLink, ProjectFile, SearchResult } from "@/lib/types";
import { cn, formatBytes, formatDateTime, timeAgo } from "@/lib/utils";
import { entityPath } from "../NotificationsButton";
import { entityIcons, SearchResultRow, useSearch } from "../SearchDialog";
import { Button, Field, Spinner } from "../ui";
import { useFeedback } from "../ui/feedback";
import { Modal } from "../ui/Modal";
import { MenuDivider, MenuItem, Popover } from "../ui/Popover";

export function fileKind(mime: string, name: string) {
  const ext = name.split(".").pop()?.toLowerCase() ?? "";
  if (mime.startsWith("image/")) return "image";
  if (mime === "application/pdf") return "pdf";
  if (["doc", "docx", "odt", "rtf", "txt", "md"].includes(ext)) return "doc";
  if (["xls", "xlsx", "csv", "ods"].includes(ext)) return "sheet";
  return "other";
}

const previewable = new Set(["image/png", "image/jpeg", "image/gif", "image/webp", "image/avif", "application/pdf"]);

export function FileIcon({ file, className }: { file: Pick<ProjectFile, "mimeType" | "name">; className?: string }) {
  const kind = fileKind(file.mimeType, file.name);
  const Icon = { image: PhotoIcon, pdf: DocumentTextIcon, doc: DocumentTextIcon, sheet: TableCellsIcon, other: DocumentIcon }[kind];
  const color = { image: "text-[var(--dot-purple)]", pdf: "text-[var(--dot-red)]", doc: "text-[var(--dot-blue)]", sheet: "text-[var(--dot-green)]", other: "text-muted" }[kind];
  return <Icon className={cn("size-5 shrink-0", color, className)} />;
}

function EntityPicker({ onPick, exclude, children }: { onPick: (r: SearchResult) => void; exclude: string[]; children: (t: { ref: React.RefObject<HTMLButtonElement | null>; onClick: (e: React.MouseEvent) => void }) => React.ReactNode }) {
  const [q, setQ] = useState("");
  const results = useSearch(q);
  return (
    <Popover className="w-80 p-1" trigger={children} onOpenChange={(o) => !o && setQ("")}>
      {(close) => (
        <>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search epics, sprints, tasks…" className="field mb-1" dir="auto" />
          {!q.trim() && <div className="px-2 py-3 text-sm text-faint">Start typing to find an item</div>}
          {q.trim() &&
            results.data
              ?.filter((r) => !exclude.includes(r.id))
              .map((r) => (
                <SearchResultRow
                  key={r.id}
                  r={r}
                  onClick={() => {
                    onPick(r);
                    close();
                  }}
                />
              ))}
          {q.trim() && results.data?.length === 0 && <div className="px-2 py-3 text-sm text-faint">No matches</div>}
        </>
      )}
    </Popover>
  );
}

function LinkChip({ link, onRemove }: { link: FileLink; onRemove?: () => void }) {
  const { pid } = useProject();
  const Icon = entityIcons[link.entityType];
  return (
    <span className="inline-flex max-w-56 items-center gap-1 rounded border border-line px-1.5 py-0.5 text-xs text-muted">
      <Icon className="size-3 shrink-0" />
      <Link to={entityPath(pid, link.entityType, link.entityId)} className="truncate hover:text-fg hover:underline" dir="auto" onClick={(e) => e.stopPropagation()}>
        {link.title}
      </Link>
      {onRemove && (
        <button type="button" onClick={onRemove} className="hover:text-fg" aria-label="Remove link">
          <XMarkIcon className="size-3" />
        </button>
      )}
    </span>
  );
}

export function UploadDialog({
  open,
  onClose,
  initialLinks = [],
  initialFiles = [],
}: {
  open: boolean;
  onClose: () => void;
  initialLinks?: FileLink[];
  initialFiles?: File[];
}) {
  const { pid } = useProject();
  const qc = useQueryClient();
  const { toast } = useFeedback();
  const [files, setFiles] = useState<File[]>(initialFiles);
  const [description, setDescription] = useState("");
  const [links, setLinks] = useState<FileLink[]>(initialLinks);
  const [progress, setProgress] = useState<{ index: number; pct: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const reset = () => {
    setFiles([]);
    setDescription("");
    setLinks(initialLinks);
    setProgress(null);
  };

  const upload = async () => {
    for (let i = 0; i < files.length; i++) {
      setProgress({ index: i, pct: 0 });
      try {
        await api.uploadFile(pid, files[i], description.trim(), links.map((l) => l.entityId), (pct) => setProgress({ index: i, pct }));
      } catch (err) {
        toast(`${files[i].name}: ${errorMessage(err)}`);
        setFiles(files.slice(i));
        setProgress(null);
        qc.invalidateQueries({ queryKey: keys.project(pid) });
        return;
      }
    }
    toast(files.length === 1 ? "File uploaded" : `${files.length} files uploaded`, "success");
    qc.invalidateQueries({ queryKey: keys.project(pid) });
    reset();
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        if (!progress) {
          reset();
          onClose();
        }
      }}
      title="Upload files"
      footer={
        <>
          <Button onClick={onClose} disabled={!!progress}>
            Cancel
          </Button>
          <Button variant="primary" icon={ArrowUpTrayIcon} onClick={upload} disabled={!files.length} loading={!!progress}>
            Upload{files.length > 1 ? ` ${files.length} files` : ""}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragging(false);
            setFiles((f) => [...f, ...Array.from(e.dataTransfer.files)]);
          }}
          onClick={() => inputRef.current?.click()}
          className={cn(
            "flex cursor-pointer flex-col items-center gap-1 rounded-lg border-2 border-dashed border-line px-4 py-6 text-center hover:bg-hover",
            dragging && "border-accent bg-accent/5",
          )}
        >
          <ArrowUpTrayIcon className="size-6 text-muted" />
          <div className="text-sm">Drop files here or click to choose</div>
          <div className="text-xs text-faint">PDF, Word documents, images and more</div>
          <input
            ref={inputRef}
            type="file"
            multiple
            hidden
            onChange={(e) => {
              setFiles((f) => [...f, ...Array.from(e.target.files ?? [])]);
              e.target.value = "";
            }}
          />
        </div>
        {files.length > 0 && (
          <ul className="space-y-1">
            {files.map((f, i) => (
              <li key={`${f.name}-${i}`} className="flex items-center gap-2 rounded-md bg-hover px-2 py-1.5 text-sm">
                <FileIcon file={{ mimeType: f.type, name: f.name }} className="size-4" />
                <span className="min-w-0 flex-1 truncate" dir="auto">
                  {f.name}
                </span>
                <span className="text-xs text-faint">{formatBytes(f.size)}</span>
                {progress?.index === i ? (
                  <span className="w-10 text-end text-xs text-accent tabular-nums">{progress.pct}%</span>
                ) : (
                  !progress && (
                    <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-faint hover:text-fg">
                      <XMarkIcon className="size-4" />
                    </button>
                  )
                )}
              </li>
            ))}
          </ul>
        )}
        <Field label="Description">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} className="field resize-none" dir="auto" placeholder="What is this file about?" />
        </Field>
        <div className="space-y-1">
          <span className="text-[13px] font-medium text-muted">Related to</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {links.map((l) => (
              <LinkChip key={l.entityId} link={l} onRemove={() => setLinks(links.filter((x) => x.entityId !== l.entityId))} />
            ))}
            <EntityPicker exclude={links.map((l) => l.entityId)} onPick={(r) => setLinks([...links, { entityType: r.entityType, entityId: r.id, title: r.title }])}>
              {(t) => (
                <button ref={t.ref} onClick={t.onClick} type="button" className="inline-flex items-center gap-1 rounded border border-dashed border-line px-1.5 py-0.5 text-xs text-muted hover:bg-hover">
                  <PlusIcon className="size-3" /> Link epic, sprint or task
                </button>
              )}
            </EntityPicker>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function EditFileDialog({ file, onClose }: { file: ProjectFile; onClose: () => void }) {
  const { pid } = useProject();
  const qc = useQueryClient();
  const [name, setName] = useState(file.name);
  const [description, setDescription] = useState(file.description);
  const [saving, setSaving] = useState(false);
  const refresh = () => qc.invalidateQueries({ queryKey: keys.project(pid) });
  const run = async (p: Promise<unknown>) => {
    try {
      await p;
    } catch (err) {
      window.dispatchEvent(new CustomEvent("app:error", { detail: err }));
    }
    await refresh();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Edit file"
      footer={
        <>
          <Button onClick={onClose}>Close</Button>
          <Button
            variant="primary"
            loading={saving}
            onClick={async () => {
              setSaving(true);
              await run(api.updateFile(pid, file.id, { name, description }));
              setSaving(false);
              onClose();
            }}
          >
            Save
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field label="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} className="field" dir="auto" />
        </Field>
        <Field label="Description">
          <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="field resize-none" dir="auto" />
        </Field>
        <div className="space-y-1">
          <span className="text-[13px] font-medium text-muted">Related to</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {file.links.map((l) => (
              <LinkChip key={l.entityId} link={l} onRemove={() => run(api.unlinkFile(pid, file.id, l.entityId))} />
            ))}
            <EntityPicker exclude={file.links.map((l) => l.entityId)} onPick={(r) => run(api.linkFile(pid, file.id, r.id))}>
              {(t) => (
                <button ref={t.ref} onClick={t.onClick} type="button" className="inline-flex items-center gap-1 rounded border border-dashed border-line px-1.5 py-0.5 text-xs text-muted hover:bg-hover">
                  <PlusIcon className="size-3" /> Link item
                </button>
              )}
            </EntityPicker>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function PreviewDialog({ file, onClose }: { file: ProjectFile; onClose: () => void }) {
  const { pid } = useProject();
  const url = api.fileUrl(pid, file.id);
  return (
    <Modal open onClose={onClose} title={<span dir="auto">{file.name}</span>} size="xl">
      {file.mimeType === "application/pdf" ? (
        <iframe src={url} title={file.name} className="h-[70vh] w-full rounded-md border border-line bg-white" />
      ) : (
        <img src={url} alt={file.name} className="mx-auto max-h-[70vh] rounded-md" />
      )}
      {file.description && (
        <p className="mt-3 text-sm whitespace-pre-wrap text-muted" dir="auto">
          {file.description}
        </p>
      )}
    </Modal>
  );
}

function FileActions({ file, onPreview, onEdit, entityId }: { file: ProjectFile; onPreview: () => void; onEdit: () => void; entityId?: string }) {
  const { pid, canEdit } = useProject();
  const qc = useQueryClient();
  const { confirm } = useFeedback();
  return (
    <Popover
      placement="bottom-end"
      className="w-48 p-1"
      trigger={(t) => (
        <button ref={t.ref} onClick={t.onClick} type="button" className="rounded p-1 text-faint hover:bg-hover hover:text-fg" aria-label="File actions">
          <EllipsisHorizontalIcon className="size-4" />
        </button>
      )}
    >
      {(close) => (
        <>
          {previewable.has(file.mimeType) && (
            <MenuItem icon={EyeIcon} onClick={() => (close(), onPreview())}>
              Preview
            </MenuItem>
          )}
          <MenuItem icon={ArrowDownTrayIcon} onClick={() => (close(), window.open(api.fileUrl(pid, file.id, true), "_blank"))}>
            Download
          </MenuItem>
          <MenuItem
            icon={LinkIcon}
            onClick={() => {
              close();
              navigator.clipboard?.writeText(new URL(api.fileUrl(pid, file.id), window.location.origin).toString());
            }}
          >
            Copy link
          </MenuItem>
          {canEdit && (
            <>
              <MenuItem icon={PencilIcon} onClick={() => (close(), onEdit())}>
                Edit details & links
              </MenuItem>
              {entityId && (
                <MenuItem
                  icon={XMarkIcon}
                  onClick={async () => {
                    close();
                    await api.unlinkFile(pid, file.id, entityId).catch((err) => window.dispatchEvent(new CustomEvent("app:error", { detail: err })));
                    qc.invalidateQueries({ queryKey: keys.project(pid) });
                  }}
                >
                  Detach from here
                </MenuItem>
              )}
              <MenuDivider />
              <MenuItem
                icon={TrashIcon}
                danger
                onClick={async () => {
                  close();
                  const ok = await confirm({
                    title: `Delete "${file.name}"?`,
                    message: "The file will be permanently removed from the project and from every linked item.",
                    confirmLabel: "Delete file",
                    danger: true,
                  });
                  if (!ok) return;
                  await api.deleteFile(pid, file.id).catch((err) => window.dispatchEvent(new CustomEvent("app:error", { detail: err })));
                  qc.invalidateQueries({ queryKey: keys.project(pid) });
                }}
              >
                Delete
              </MenuItem>
            </>
          )}
        </>
      )}
    </Popover>
  );
}

export function FileList({ files, entityId, compact }: { files: ProjectFile[]; entityId?: string; compact?: boolean }) {
  const { pid, userName } = useProject();
  const [preview, setPreview] = useState<ProjectFile | null>(null);
  const [editing, setEditing] = useState<ProjectFile | null>(null);

  const open = (f: ProjectFile) => (previewable.has(f.mimeType) ? setPreview(f) : window.open(api.fileUrl(pid, f.id, true), "_blank"));

  return (
    <>
      <ul className="divide-y divide-line rounded-lg border border-line">
        {files.map((f) => (
          <li key={f.id} className="group flex items-start gap-3 px-3 py-2.5 hover:bg-hover/60">
            {fileKind(f.mimeType, f.name) === "image" && !compact ? (
              <button type="button" onClick={() => open(f)} className="size-10 shrink-0 overflow-hidden rounded border border-line">
                <img src={api.fileUrl(pid, f.id)} alt="" loading="lazy" className="size-full object-cover" />
              </button>
            ) : (
              <button type="button" onClick={() => open(f)} className={cn("flex shrink-0 items-center justify-center rounded bg-hover", compact ? "size-8" : "size-10")}>
                <FileIcon file={f} />
              </button>
            )}
            <div className="min-w-0 flex-1">
              <button type="button" onClick={() => open(f)} className="block max-w-full truncate text-start text-sm font-medium hover:underline" dir="auto">
                {f.name}
              </button>
              {f.description && (
                <p className="line-clamp-2 text-[13px] text-muted" dir="auto">
                  {f.description}
                </p>
              )}
              <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs text-faint">
                <span>{formatBytes(f.sizeBytes)}</span>
                <span>·</span>
                <span dir="auto">{userName(f.uploadedBy)}</span>
                <span>·</span>
                <span title={formatDateTime(f.createdAt)}>{timeAgo(f.createdAt)}</span>
                {!compact && f.links.map((l) => <LinkChip key={l.entityId} link={l} />)}
              </div>
            </div>
            <FileActions file={f} entityId={entityId} onPreview={() => setPreview(f)} onEdit={() => setEditing(f)} />
          </li>
        ))}
      </ul>
      {preview && <PreviewDialog file={preview} onClose={() => setPreview(null)} />}
      {editing && <EditFileDialog file={files.find((f) => f.id === editing.id) ?? editing} onClose={() => setEditing(null)} />}
    </>
  );
}

/** Files attached to a single epic, sprint or task. */
export function EntityFiles({ entityType, entityId, title }: { entityType: EntityType; entityId: string; title: string }) {
  const { pid, canEdit } = useProject();
  const files = useFiles(pid, entityId);
  const [uploading, setUploading] = useState(false);
  const qc = useQueryClient();

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-muted">
          <PaperClipIcon className="size-4" /> Files {files.data?.length ? <span className="font-normal text-faint">{files.data.length}</span> : null}
        </h3>
        {canEdit && (
          <div className="flex items-center gap-1">
            <AttachExisting
              exclude={files.data?.map((f) => f.id) ?? []}
              onPick={async (fileId) => {
                await api.linkFile(pid, fileId, entityId).catch((err) => window.dispatchEvent(new CustomEvent("app:error", { detail: err })));
                qc.invalidateQueries({ queryKey: keys.project(pid) });
              }}
            />
            <Button size="sm" variant="ghost" icon={ArrowUpTrayIcon} onClick={() => setUploading(true)}>
              Upload
            </Button>
          </div>
        )}
      </div>
      {files.isPending && <Spinner className="size-4" />}
      {files.data && files.data.length > 0 && <FileList files={files.data} entityId={entityId} compact />}
      {files.data?.length === 0 && <div className="rounded-lg border border-dashed border-line px-3 py-3 text-sm text-faint">No files attached</div>}
      <UploadDialog open={uploading} onClose={() => setUploading(false)} initialLinks={[{ entityType, entityId, title }]} key={entityId} />
    </div>
  );
}

function AttachExisting({ exclude, onPick }: { exclude: string[]; onPick: (fileId: string) => void }) {
  const { pid } = useProject();
  const all = useFiles(pid);
  const [q, setQ] = useState("");
  const items = (all.data ?? []).filter((f) => !exclude.includes(f.id) && f.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <Popover
      placement="bottom-end"
      className="w-80 p-1"
      trigger={(t) => (
        <Button ref={t.ref} onClick={t.onClick} size="sm" variant="ghost" icon={LinkIcon}>
          Attach existing
        </Button>
      )}
    >
      {(close) => (
        <>
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search project files…" className="field mb-1" dir="auto" />
          {items.slice(0, 30).map((f) => (
            <button
              key={f.id}
              type="button"
              onClick={() => {
                onPick(f.id);
                close();
              }}
              className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-start text-sm hover:bg-hover"
            >
              <FileIcon file={f} className="size-4" />
              <span className="truncate" dir="auto">
                {f.name}
              </span>
            </button>
          ))}
          {items.length === 0 && <div className="px-2 py-3 text-sm text-faint">No other files in this project</div>}
        </>
      )}
    </Popover>
  );
}

